import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Users, Car, Flame, Star, Calendar, ArrowLeft, Heart, Share2, Bell, Mail, Signal, Sparkles, PawPrint, Leaf, Footprints, Mountain, Volume2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MapComponent from '../components/MapComponent';
import MarkdownText from '../components/MarkdownText';
import { locationsAPI, reviewsAPI, favoritesAPI, eventsAPI, interestAPI } from '../utils/api';
import { toast } from 'sonner';

const LocationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [favBusy, setFavBusy] = useState(false);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [events, setEvents] = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);
  // Lightbox (gallery viewer) state. `lightboxIdx === null` means closed.
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [locResp, reviewsResp, eventsResp] = await Promise.all([
          locationsAPI.getById(id),
          reviewsAPI.getLocationReviews(id).catch(() => ({ data: [] })),
          eventsAPI.listByLocation(id).catch(() => ({ data: [] })),
        ]);
        setLocation(locResp.data);
        const sorted = [...(reviewsResp.data || [])].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setReviews(sorted);
        setEvents(eventsResp.data || []);
      } catch (error) {
        console.error('Error fetching location:', error);
        toast.error('Nepodařilo se načíst detail místa');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Load favorite status if user logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    favoritesAPI.listIds()
      .then((res) => setIsFavorite(res.data.includes(id)))
      .catch((err) => console.error('fav check failed', err));
  }, [id]);

  // Lightbox keyboard navigation – arrows and Esc.
  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const photos = location?.photos || [];
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      else if (e.key === 'ArrowLeft') setLightboxIdx((i) => (i === null ? i : (i - 1 + photos.length) % photos.length));
      else if (e.key === 'ArrowRight') setLightboxIdx((i) => (i === null ? i : (i + 1) % photos.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, location]);

  const handleToggleFavorite = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.info('Pro ukládání oblíbených míst se přihlaste');
      navigate('/login');
      return;
    }
    try {
      setFavBusy(true);
      if (isFavorite) {
        await favoritesAPI.remove(id);
        setIsFavorite(false);
        toast.success('Odebráno z oblíbených');
      } else {
        await favoritesAPI.add(id);
        setIsFavorite(true);
        toast.success('Přidáno do oblíbených ❤️');
      }
    } catch (err) {
      console.error(err);
      toast.error('Nepodařilo se aktualizovat oblíbené');
    } finally {
      setFavBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center bg-white/90 px-8 py-6 rounded-xl">
            <p className="text-xl">Načítání...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (!location) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center bg-white/95 px-10 py-8 rounded-xl shadow-2xl">
            <h2 className="text-2xl font-bold mb-4">Místo nenalezeno</h2>
            <Button onClick={() => navigate('/locations')}>Zpět na místa</Button>
          </div>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Odkaz zkopírován do schránky!');
  };

  const handleBooking = () => {
    const currentUser = localStorage.getItem('currentUser');
    if (!currentUser) {
      toast.error('Pro rezervaci se musíte přihlásit');
      navigate('/login');
      return;
    }
    navigate(`/booking/${location.id}`);
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 hover:bg-gray-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title and Actions */}
            <div>
              {/* Solid dark card behind the title so it's readable against any
                  background image (preview screenshot showed it disappearing). */}
              <div className="bg-gray-900/85 backdrop-blur-sm text-white rounded-2xl p-5 mb-4 shadow-xl">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2" data-testid="location-title">{location.name}</h1>
                    <div className="flex items-center gap-2 text-orange-200">
                      <MapPin className="w-5 h-5" />
                      <span>{location.type || location.category}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleToggleFavorite}
                      disabled={favBusy}
                      data-testid="detail-favorite-btn"
                      aria-label={isFavorite ? 'Odebrat z oblíbených' : 'Přidat do oblíbených'}
                      className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
                    >
                      <Heart className={`w-5 h-5 transition-colors ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleShare}
                      className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
                    >
                      <Share2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Photo gallery: hero + thumbnail strip. Click anywhere to open lightbox. */}
              {(() => {
                const photos = location.photos && location.photos.length > 0
                  ? location.photos
                  : [location.image || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4'];
                return (
                  <div className="mb-6" data-testid="photo-gallery">
                    <button
                      type="button"
                      onClick={() => setLightboxIdx(0)}
                      className="block w-full rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-orange-400 group relative"
                      aria-label="Zvětšit fotku"
                    >
                      <img
                        src={photos[0]}
                        alt={location.name}
                        className="w-full h-96 object-cover group-hover:scale-[1.02] transition-transform duration-300"
                      />
                      {photos.length > 1 && (
                        <span className="absolute bottom-3 right-3 bg-black/70 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                          📷 {photos.length} fotek – klikněte pro prohlížení
                        </span>
                      )}
                    </button>
                    {photos.length > 1 && (
                      <div
                        className="mt-3 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x"
                        data-testid="photo-thumbs"
                      >
                        {photos.map((src, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setLightboxIdx(i)}
                            className="flex-shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 border-transparent hover:border-orange-500 focus:border-orange-500 focus:outline-none transition snap-start"
                            aria-label={`Zobrazit fotku ${i + 1}`}
                            data-testid={`photo-thumb-${i}`}
                          >
                            <img src={src} alt={`${location.name} – ${i + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Description */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4">O místě</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap" data-testid="location-full-description">
                  {(location.full_description || location.fullDescription || location.description) ? (
                    <MarkdownText text={location.full_description || location.fullDescription || location.description} />
                  ) : 'Hostitel zatím nedoplnil popis.'}
                </p>
              </CardContent>
            </Card>

            {/* Facilities */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4">Vybavení a možnosti</h2>
                <div className="flex flex-wrap gap-2">
                  {location.facilities.map((facility, idx) => (
                    <Badge key={idx} variant="secondary" className="px-4 py-2 text-sm">
                      {facility}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Details */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4">Detaily</h2>
                <div className="grid grid-cols-2 gap-4">
                  <DetailItem
                    icon={<Users className="w-5 h-5" />}
                    label="Kapacita"
                    value={`${location.max_capacity || location.maxCapacity} osob`}
                  />
                  <DetailItem
                    icon={<MapPin className="w-5 h-5" />}
                    label="Terén"
                    value={location.terrain}
                  />
                  <div className="flex items-start gap-3 col-span-2" data-testid="accessibility-list">
                    <div className="text-orange-600 mt-0.5"><Car className="w-5 h-5" /></div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Dostupnost</p>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {(location.category || location.accessibility || '').split(',').map((a) => a.trim()).filter(Boolean).map((a, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center bg-orange-100 text-orange-700 text-xs font-medium px-2.5 py-1 rounded-full"
                          >
                            {a}
                          </span>
                        ))}
                        {!location.category && !location.accessibility && <span className="text-sm text-gray-400">—</span>}
                      </div>
                    </div>
                  </div>
                  {location.parking_type && (
                    <div className="flex items-start gap-3 col-span-2" data-testid="parking-info">
                      <div className="text-orange-600 mt-0.5">🅿️</div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Parkování</p>
                        <p className="text-sm font-medium text-gray-800 mt-1">
                          {location.parking_type === 'on_site' && 'Přímo na pozemku'}
                          {location.parking_type === 'off_site' && (
                            <>
                              Mimo pozemek
                              {location.parking_distance_m > 0 && (
                                <span className="text-gray-600"> – vzdálenost cca <strong>{location.parking_distance_m} m</strong> od místa</span>
                              )}
                            </>
                          )}
                          {location.parking_type === 'none' && 'Bez parkování'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Environment (from owner's profile) */}
            <Card data-testid="environment-card">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4">Prostředí</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DetailItem
                    icon={<Signal className="w-5 h-5" />}
                    label="Mobilní signál"
                    value={location.mobile_signal || '—'}
                  />
                  <DetailItem
                    icon={<Footprints className="w-5 h-5" />}
                    label="Pohyb turistů v okolí"
                    value={location.tourist_traffic || '—'}
                  />
                  <RatingItem
                    icon={<Sparkles className="w-5 h-5" />}
                    label="Pozorování hvězd"
                    rating={location.stargazing_rating}
                  />
                  <RatingItem
                    icon={<PawPrint className="w-5 h-5" />}
                    label="Pozorování zvěře"
                    rating={location.wildlife_rating}
                  />
                  <RatingItem
                    icon={<Leaf className="w-5 h-5" />}
                    label="Míra samoty"
                    rating={location.privacy_rating}
                  />
                  <RatingItem
                    icon={<Mountain className="w-5 h-5" />}
                    label="Výhled do přírody"
                    rating={location.view_rating}
                  />
                  <RatingItem
                    icon={<Volume2 className="w-5 h-5" />}
                    label="Zvuky přírody"
                    rating={location.sounds_rating}
                  />
                </div>

                {(location.check_in_time || location.check_out_time) && (
                  <div className="mt-5 pt-5 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="check-times-block">
                    <DetailItem
                      icon={<Calendar className="w-5 h-5" />}
                      label="Příjezd od"
                      value={location.check_in_time || '—'}
                    />
                    <DetailItem
                      icon={<Calendar className="w-5 h-5" />}
                      label="Odjezd nejpozději do"
                      value={location.check_out_time || '—'}
                    />
                  </div>
                )}

                {/* Open access + fire policy – informative, from host's profile */}
                <div className="mt-5 pt-5 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {typeof location.open_access === 'boolean' && (
                    <DetailItem
                      icon={<Car className="w-5 h-5" />}
                      label="Přístup na pozemek"
                      value={location.open_access ? 'Volný (bez brány / pokynů)' : 'Po pokynech od hostitele'}
                    />
                  )}
                  {typeof location.fire_allowed === 'boolean' && (
                    <DetailItem
                      icon={<Flame className="w-5 h-5" />}
                      label="Rozdělání ohně"
                      value={location.fire_allowed ? 'ANO – povoleno' : 'NE – zakázáno'}
                    />
                  )}
                </div>
                {location.fire_allowed && (
                  <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="fire-disclaimer">
                    ⚠️ <b>Upozornění:</b> Hostitel rozdělání ohně povoluje, ale rozdělávání ohně může být v daný okamžik <b>regulováno státem</b> (období sucha, zákazy KHK / hasičů, NP a CHKO atd.). Zkontrolujte si prosím vždy aktuální místní pravidla a hlášení hasičského záchranného sboru před návštěvou.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Nearby attractions – owner's tips */}
            {Array.isArray(location.nearby_attractions) && location.nearby_attractions.length > 0 && (
              <Card data-testid="nearby-attractions-card">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-3">Zajímavá místa v okolí</h2>
                  <p className="text-sm text-gray-500 mb-4">Tipy přímo od majitele pozemku.</p>
                  <ul className="space-y-2">
                    {location.nearby_attractions
                      .filter((n) => n && (n.title || n.url))
                      .map((n, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-orange-600 mt-1">📍</span>
                          <div>
                            <div className="font-medium">{n.title || n.url}</div>
                            {n.url && (
                              <a
                                href={n.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-orange-600 hover:underline break-all"
                              >
                                {n.url}
                              </a>
                            )}
                          </div>
                        </li>
                      ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Reviews moved below the map (per user request) */}

            {/* House rules: NOT shown publicly. They are bundled into the
                arrival-instructions email after booking confirmation. */}

            {/* Upcoming events at this location */}
            {events.length > 0 && (
              <Card data-testid="upcoming-events-card">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-orange-600" />
                    Plánované organizované akce ({events.length})
                  </h2>
                  <div className="space-y-3">
                    {events.map((e) => (
                      <div
                        key={e.id}
                        className="border border-orange-200 bg-orange-50 rounded-lg p-4 cursor-pointer hover:bg-orange-100 transition-colors flex gap-3"
                        onClick={() => navigate(`/event/${e.id}`)}
                        data-testid={`event-card-${e.id}`}
                      >
                        {e.photos?.[0] && (
                          <img
                            src={e.photos[0]}
                            alt={e.name}
                            className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-md flex-shrink-0"
                            loading="lazy"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                            <h3 className="font-bold text-lg">{e.name}</h3>
                            <Badge className="bg-orange-600 text-white">
                              {e.price_per_person} Kč / osoba
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-700 mb-2">
                            📅 {e.start_date} → {e.end_date} &middot; 👥 max {e.max_persons} osob
                          </p>
                          {e.description && <p className="text-sm text-gray-600"><MarkdownText text={e.description} /></p>}
                          <p className="text-xs text-orange-600 mt-2">Klikněte pro detail →</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Owner – only name shown publicly. Email/phone are sent in confirmation email. */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-3">Hostitel</h2>
                <div className="flex items-center gap-3 mb-2">
                  {location.owner?.avatar ? (
                    <img
                      src={location.owner.avatar}
                      alt={location.owner?.name || 'Hostitel'}
                      className="w-16 h-16 rounded-full object-cover border-2 border-orange-200"
                      data-testid="host-avatar"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold text-xl border-2 border-orange-200">
                      {(location.owner?.name || '?').trim()[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="font-semibold text-lg" data-testid="host-first-name">
                    {(location.owner?.name || '').trim().split(/\s+/)[0]}
                  </span>
                </div>
                {location.no_caravans && (
                  <div
                    className="mt-3 inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-1.5 rounded-full text-sm font-semibold"
                    data-testid="owner-no-caravans-badge"
                  >
                    <span aria-hidden>🚫</span>
                    Majitel neakceptuje karavany
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-2">
                  Kontaktní údaje hostitele obdržíte v e-mailu po potvrzení rezervace.
                </p>
              </CardContent>
            </Card>

            {/* Map */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4">Poloha</h2>
                <MapComponent 
                  locations={[location]}
                  center={[location.latitude, location.longitude]}
                  zoom={13}
                  height="400px"
                  selectedLocationId={location.id}
                />
                <p className="text-sm text-gray-600 mt-3">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  GPS: {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                </p>
              </CardContent>
            </Card>

            {/* Reviews (moved to the bottom – below the map) */}
            <Card data-testid="reviews-section">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Star className="w-6 h-6 fill-orange-500 text-orange-500" />
                    Hodnocení hostů
                    {reviews.length > 0 && (
                      <span className="text-base font-normal text-gray-600">
                        ({(reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)} / 5 · {reviews.length})
                      </span>
                    )}
                  </h2>
                </div>

                {reviews.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    Toto místo zatím nemá žádná zveřejněná hodnocení. Buďte první! 🌲
                  </p>
                ) : (
                  <>
                    <div className="space-y-5">
                      {(showAllReviews ? reviews : reviews.slice(0, 5)).map((review) => (
                        <div key={review.id} className="border-b border-gray-100 pb-5 last:border-0 last:pb-0">
                          <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
                            <div>
                              <span className="font-semibold text-gray-900">{review.reviewer_name}</span>
                              <div className="flex items-center gap-1 mt-1">
                                {[1, 2, 3, 4, 5].map((s) => (
                                  <Star
                                    key={s}
                                    className={`w-4 h-4 ${
                                      s <= review.rating
                                        ? 'fill-orange-500 text-orange-500'
                                        : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                            <span className="text-sm text-gray-500">
                              {review.created_at ? new Date(review.created_at).toLocaleDateString('cs-CZ') : ''}
                            </span>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">{review.comment}</p>
                        </div>
                      ))}
                    </div>
                    {reviews.length > 5 && (
                      <div className="mt-6 text-center">
                        <Button
                          variant="outline"
                          onClick={() => setShowAllReviews((v) => !v)}
                          data-testid="toggle-all-reviews"
                        >
                          {showAllReviews ? 'Zobrazit méně' : `Zobrazit všechna hodnocení (${reviews.length})`}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 shadow-lg">
              <CardContent className="p-6">
                <div className="mb-6">
                  {location.pricing_mode === 'per_unit' ? (
                    <div data-testid="price-breakdown" className="space-y-1 mb-2">
                      <p className="font-semibold mb-1">Ceník (Kč / noc):</p>
                      {[
                        { label: 'Dospělý', val: location.price_per_adult },
                        { label: 'Dítě', val: location.price_per_child },
                        { label: 'Auto', val: location.price_per_car },
                        { label: 'Stan', val: location.price_per_tent },
                        { label: 'Chata / maringotka / teepee – objekt', val: location.price_per_cabin_unit },
                        { label: 'Chata / maringotka / teepee (za osobu)', val: location.price_per_cabin },
                      ].filter(x => x.val > 0).map(({ label, val }) => (
                        <div key={label} className="flex justify-between text-sm">
                          <span className="text-gray-700">{label}</span>
                          <span className="font-semibold">{val} Kč</span>
                        </div>
                      ))}
                      {(location.price_per_cabin > 0 || location.price_per_cabin_unit > 0) && (location.price_per_adult > 0 || location.price_per_child > 0) && (
                        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5 mt-1.5" data-testid="cabin-pricing-note">
                          💡 Pokud zvolíte ubytování v <strong>chatě/maringotce/teepee</strong>, neúčtuje se navíc cena za dospělého ani dítě.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-3xl font-bold">{location.price} Kč</span>
                      <span className="text-gray-600">/ noc</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold">
                      {location.reviews?.length > 0 ? '5.0' : 'Nové'}
                    </span>
                    {location.reviews?.length > 0 && (
                      <span className="text-gray-600">({location.reviews.length} hodnocení)</span>
                    )}
                  </div>
                </div>

                {location.host_ready === false ? (
                  <ComingSoonBlock locationId={location.id} locationName={location.name} />
                ) : (
                  <>
                    <Button
                      onClick={handleBooking}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6 text-lg mb-4"
                      data-testid="reserve-btn"
                    >
                      <Calendar className="w-5 h-5 mr-2" />
                      Rezervovat
                    </Button>
                  </>
                )}

                <div className="mt-6 pt-6 border-t space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Maximální kapacita</span>
                    <span className="font-semibold">{location.max_capacity || location.maxCapacity || '—'} osob</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Dostupnost</span>
                    <span className="font-semibold text-green-600">Volné</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />

      {/* Lightbox – fullscreen photo viewer with prev/next arrows.
          Keyboard: Esc closes, ←/→ navigate. */}
      {lightboxIdx !== null && location?.photos && location.photos.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxIdx(null)}
          data-testid="lightbox"
          role="dialog"
          aria-modal="true"
        >
          {/* Close */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-4 right-4 text-white hover:bg-white/10 rounded-full p-2"
            aria-label="Zavřít"
            data-testid="lightbox-close"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Prev */}
          {location.photos.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i - 1 + location.photos.length) % location.photos.length); }}
              className="absolute left-2 md:left-6 text-white bg-white/10 hover:bg-white/25 rounded-full p-3 transition"
              aria-label="Předchozí fotka"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {/* Image */}
          <img
            src={location.photos[lightboxIdx]}
            alt={`${location.name} – fotka ${lightboxIdx + 1}`}
            className="max-w-full max-h-[90vh] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          {/* Next */}
          {location.photos.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i + 1) % location.photos.length); }}
              className="absolute right-2 md:right-6 text-white bg-white/10 hover:bg-white/25 rounded-full p-3 transition"
              aria-label="Další fotka"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}

          {/* Counter */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white text-sm bg-black/60 px-4 py-1.5 rounded-full">
            {lightboxIdx + 1} / {location.photos.length}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="text-orange-600 mt-1">{icon}</div>
    <div>
      <div className="text-sm text-gray-500">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  </div>
);

const RatingItem = ({ icon, label, rating }) => {
  const value = Math.max(0, Math.min(5, parseInt(rating) || 0));
  return (
    <div className="flex items-start gap-3" data-testid={`rating-${label}`}>
      <div className="text-orange-600 mt-1">{icon}</div>
      <div>
        <div className="text-sm text-gray-500">{label}</div>
        <div className="flex items-center gap-0.5 mt-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star
              key={s}
              className={`w-4 h-4 ${s <= value ? 'fill-orange-500 text-orange-500' : 'text-gray-300'}`}
            />
          ))}
          <span className="text-xs text-gray-500 ml-1">{value}/5</span>
        </div>
      </div>
    </div>
  );
};

const ComingSoonBlock = ({ locationId, locationName }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Zadejte platný e-mail');
      return;
    }
    try {
      setSubmitting(true);
      await interestAPI.signup({ location_id: locationId, email });
      setDone(true);
      toast.success('Hotovo! Napíšeme vám, jakmile pozemek půjde rezervovat.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chyba');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-5 mb-4" data-testid="coming-soon-block">
      <div className="flex items-center gap-2 mb-2">
        <Bell className="w-5 h-5 text-orange-600" />
        <h3 className="font-bold text-orange-900">Brzy dostupné k rezervaci</h3>
      </div>
      <p className="text-sm text-orange-800 mb-3 leading-relaxed">
        Hostitel právě dokončuje nastavení platební brány. Nechte nám e-mail a my vás upozorníme, jakmile půjde <strong>{locationName}</strong> rezervovat.
      </p>
      {done ? (
        <div className="bg-green-100 border border-green-300 text-green-800 rounded-lg p-3 text-sm">
          ✓ Vaše přihlášení jsme přijali. Brzy vás kontaktujeme.
        </div>
      ) : (
        <form onSubmit={handleSignup} className="space-y-2">
          <div className="relative">
            <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              type="email"
              placeholder="vas@email.cz"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-9"
              data-testid="interest-email-input"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="interest-submit"
          >
            <Bell className="w-4 h-4 mr-2" />
            {submitting ? 'Ukládám…' : 'Notifikovat mě'}
          </Button>
        </form>
      )}
    </div>
  );
};

export default LocationDetail;
